/* Windowed renderer for huge terminal output.
 *
 * The normal Terminal maps every line to a DOM node — fine for typical output,
 * but a script printing tens of thousands of lines would create that many nodes
 * and drop frames on scroll. This component renders ONLY the lines in (and just
 * around) the viewport, on a fixed row height, so the DOM node count stays tiny
 * and scrolling holds 60fps regardless of total line count.
 *
 * Fixed-height windowing requires each logical line to occupy exactly one row, so
 * this path is NO-WRAP (horizontal scroll for long lines) — standard terminal
 * behaviour for large logs. Wrapping (variable height) would break the offset
 * math, so the wrapped pre-wrap Terminal is kept for normal-sized output, below
 * the virtualization threshold. */

import { useEffect, useRef, useState } from 'preact/hooks';
import type { OutputLine } from '../results/result-types';

// Must match `.term-virt-row` height in global.css (font-size 13.5 × line-height 1.7 ≈ 23).
const ROW_H = 23;
const OVERSCAN = 12;

interface VirtualizedOutputProps {
  lines: OutputLine[];
}

export function VirtualizedOutput({ lines }: VirtualizedOutputProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH, setViewH] = useState(400);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((e) => setViewH(e[0].contentRect.height));
    ro.observe(el);
    setViewH(el.clientHeight || 400);
    return () => ro.disconnect();
  }, []);

  const total = lines.length;
  const start = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const visibleCount = Math.ceil(viewH / ROW_H) + OVERSCAN * 2;
  const end = Math.min(total, start + visibleCount);
  const window = lines.slice(start, end);

  return (
    <div
      class="term term-virt"
      ref={scrollRef}
      onScroll={(e) => setScrollTop((e.currentTarget as HTMLDivElement).scrollTop)}
    >
      <div class="term-virt-spacer" style={{ height: total * ROW_H }}>
        <div class="term-virt-window" style={{ transform: `translateY(${start * ROW_H}px)` }}>
          {window.map((l, i) => (
            <div key={start + i} class={'term-virt-row' + (l.c ? ' out-' + l.c : '')}>
              {l.t === '' ? ' ' : l.t}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Render the windowed path only when output is large enough to justify it. */
export const VIRTUALIZE_THRESHOLD = 1000;
